import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const CONSENT_KEY = "savi_cookie_consent";

const loadGA4 = () => {
  if (document.getElementById("ga4-script")) return;
  const script = document.createElement("script");
  script.id = "ga4-script";
  script.async = true;
  script.src = "https://www.googletagmanager.com/gtag/js?id=G-L3JX7GPM5V";
  document.head.appendChild(script);
  script.onload = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", "G-L3JX7GPM5V");
  };
};

const loadPostHog = () => {
  if (window.posthog && window.posthog.__loaded) return;
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2===o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs",{api_host:"https://us.i.posthog.com",person_profiles:"identified_only",session_recording:{recordCrossOriginIframes:true,capturePerformance:false}});
};

const loadMetaPixel = () => {
  if (window.fbq) return;
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', '298873854179454');
  window.fbq('track', 'PageView');
};

const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const applyConsent = (consent) => {
  if (consent?.analytics) {
    loadGA4();
    loadPostHog();
  }
  if (consent?.marketing) {
    loadMetaPixel();
  }
};

const CookieConsent = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  const noTracking = /^\/sojourns(\/|$)/.test(pathname);

  useEffect(() => {
    if (noTracking) return undefined;
    const consent = getConsent();
    if (consent) {
      applyConsent(consent);
    } else {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [noTracking]);

  const saveConsent = useCallback((consent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    applyConsent(consent);
    setVisible(false);
  }, []);

  const acceptAll = () => saveConsent({ essential: true, analytics: true, marketing: true });
  const acceptSelected = () => saveConsent({ essential: true, analytics, marketing });
  const rejectAll = () => saveConsent({ essential: true, analytics: false, marketing: false });

  if (!visible || noTracking) return null;

  const hasPortalNav = ["/dashboard", "/community", "/guide", "/handbook", "/library", "/arrival", "/volunteer-schedule"].some((p) => pathname.startsWith(p));
  const hasPublicNav = typeof document !== "undefined" && !!document.querySelector('[data-testid="public-bottom-nav"]');
  // On phones the banner sits above whichever bottom bar the page has, never on top of it.
  const lift = hasPortalNav || hasPublicNav ? "pb-[calc(76px+env(safe-area-inset-bottom))] md:pb-6" : "pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-6";

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-[100] p-3 md:p-6 pointer-events-none ${lift}`} data-testid="cookie-consent-banner">
      <div className="max-w-2xl mx-auto bg-[#2E2A26] rounded-2xl shadow-2xl border border-white/10 p-4 md:p-6 pointer-events-auto">
        <div className="mb-3 md:mb-4">
          <p className="text-white text-sm font-medium mb-1 md:mb-2">We value your privacy</p>
          <p className="text-white/50 text-xs leading-relaxed">
            We use cookies to understand site traffic. Nothing runs until you say so.{" "}
            <Link to="/privacy" className="text-[#C85A38] hover:underline">Privacy</Link>
            {" · "}
            <Link to="/cookies" className="text-[#C85A38] hover:underline">Cookies</Link>
          </p>
        </div>

        {showDetails && (
          <div className="mb-4 space-y-3 border-t border-white/10 pt-4">
            <label className="flex items-center justify-between">
              <div>
                <p className="text-white text-xs font-medium">Essential</p>
                <p className="text-white/40 text-[10px]">Required for the site to function</p>
              </div>
              <div className="w-10 h-5 bg-[#C85A38] rounded-full relative cursor-not-allowed">
                <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
              </div>
            </label>
            <label className="flex items-center justify-between cursor-pointer" onClick={() => setAnalytics(!analytics)}>
              <div>
                <p className="text-white text-xs font-medium">Analytics</p>
                <p className="text-white/40 text-[10px]">Google Analytics, PostHog — helps us understand usage</p>
              </div>
              <div className={`w-10 h-5 rounded-full relative transition-colors ${analytics ? "bg-[#C85A38]" : "bg-white/20"}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${analytics ? "right-0.5" : "left-0.5"}`} />
              </div>
            </label>
            <label className="flex items-center justify-between cursor-pointer" onClick={() => setMarketing(!marketing)}>
              <div>
                <p className="text-white text-xs font-medium">Marketing</p>
                <p className="text-white/40 text-[10px]">Retargeting and ad conversion tracking</p>
              </div>
              <div className={`w-10 h-5 rounded-full relative transition-colors ${marketing ? "bg-[#C85A38]" : "bg-white/20"}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${marketing ? "right-0.5" : "left-0.5"}`} />
              </div>
            </label>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={acceptAll}
            className="flex-1 bg-[#C85A38] hover:bg-[#A84830] text-white text-xs font-medium min-h-[44px] px-4 rounded-full transition-colors"
            data-testid="cookie-accept-all"
          >
            Accept all
          </button>
          {showDetails ? (
            <button
              onClick={acceptSelected}
              className="flex-1 bg-white/10 hover:bg-white/15 text-white text-xs font-medium min-h-[44px] px-4 rounded-full transition-colors"
              data-testid="cookie-accept-selected"
            >
              Save preferences
            </button>
          ) : (
            <button
              onClick={rejectAll}
              className="flex-1 border border-white/15 hover:border-white/30 text-white/70 text-xs font-medium min-h-[44px] px-4 rounded-full transition-colors"
              data-testid="cookie-reject-all"
            >
              Reject all
            </button>
          )}
        </div>
        {!showDetails && (
          <button
            onClick={() => setShowDetails(true)}
            className="mt-2 w-full text-center text-white/45 hover:text-white/70 text-xs min-h-[36px] transition-colors"
            data-testid="cookie-customize"
          >
            Customise
          </button>
        )}
        {showDetails && (
          <button
            onClick={rejectAll}
            className="mt-2 w-full text-center text-white/45 hover:text-white/70 text-xs min-h-[36px] transition-colors"
            data-testid="cookie-reject-all"
          >
            Reject all
          </button>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
